import type { CartesianPose } from '@sb/geometry';
import { applyCompoundPose, ZERO_POSE } from '@sb/geometry';
import type { TCPFrame } from '@sb/motion-planning';
import { getTCPIndexFromTCPOption } from '@sb/motion-planning/TCPOffsetOption';

import type { DeviceKind, EquipmentItem } from '../types';

import { getCameraTooltipTransform } from './cameraTooltipTransform';
import {
  optionBelongsToKind,
  TOOLTIP_NAMING_SCHEMES,
} from './getTooltipOptions';
import {
  compareWristChainOrder,
  isEndEffector,
  isForceSensor,
  isWristChainMember,
  isWristMounted,
} from './util';

/**
 * One ordered model of everything mounted on the wrist, built once from
 * equipment rows, so that "which link", "which owner", and "what transform"
 * are answered by one rule instead of re-derived per caller. The chain is a
 * VIEW over `EquipmentItem[]` — the wire format does not change.
 *
 * Ordering is flange→tip by construction (`compareWristChainOrder`, the same
 * order the URDF generator bakes into the robot model), so consumers never
 * depend on equipment listing order. Roles are resolved once: a wrist-chain
 * member (adapter, force/torque sensor, bracket) is a `spacer`; an actuating
 * gripper is a `gripper`. Disabled gripper rows are kept — saved frames
 * resolve them by id (the URDF keeps their links for the tool-swap
 * workflow) — but only enabled rows compose or own options by name.
 */
export type ChainLink = {
  readonly item: EquipmentItem;
  readonly kind: DeviceKind;
  readonly role: 'spacer' | 'gripper';
};

export type ResolvedTCP = {
  /** ROS link name for the frame; `tool0` when no owner link exists. */
  readonly tcpLink: string;
  /** Full flange→TCP pose (spacers composed below the owner's tip). */
  readonly transformFromTool0: CartesianPose;
};

export type EndEffectorChain = {
  /** Enabled rows, flange→tip, at most one spacer per chain slot. */
  readonly links: readonly ChainLink[];
  /** Every gripper row, enabled AND disabled (id resolution needs both). */
  readonly grippers: readonly ChainLink[];
  /** The enabled force/torque sensor, when one is mounted. */
  readonly forceSensor: ChainLink | undefined;
  /** The row a frame names, or undefined when no single row can be named. */
  owner(frame: TCPFrame): ChainLink | undefined;
  /**
   * Link + composed flange→TCP pose for a frame, degrading to the flange. The
   * wrist-camera frame is answered from the camera row's own configuration
   * rather than from the chain — a camera hangs off the wrist instead of
   * sitting in it.
   */
  tcp(frame: TCPFrame): ResolvedTCP;
};

const FLANGE: ResolvedTCP = { tcpLink: 'tool0', transformFromTool0: ZERO_POSE };

/**
 * The owner's own tooltip transform for the option's index: the tooltip in
 * that SLOT for a custom gripper, the slot for a quick changer, the single
 * `tcpTransform` otherwise. `undefined` means the owner publishes no tooltip
 * at that index — an emptied slot, or one past the end — so the URDF minted
 * no matching link and callers must degrade to the flange rather than name a
 * link robot-side consumers would silently substitute.
 */
function ownTipTransform(
  item: EquipmentItem,
  tcpIndex: number,
): CartesianPose | undefined {
  const config = item.config as {
    kind: string;
    tooltips?: Array<{ tcpTransform?: CartesianPose } | null>;
    grippers?: {
      primary?: { tcpTransform?: CartesianPose };
      secondary?: { tcpTransform?: CartesianPose };
    };
    tcpTransform?: CartesianPose;
  };

  const tooltip = Array.isArray(config.tooltips)
    ? config.tooltips[tcpIndex]
    : undefined;

  if (tooltip?.tcpTransform) {
    return tooltip.tcpTransform;
  }

  if (config.kind === 'OnRobotDualQuickChanger' && config.grippers) {
    if (tcpIndex === 0) return config.grippers.primary?.tcpTransform;
    if (tcpIndex === 1) return config.grippers.secondary?.tcpTransform;

    return undefined;
  }

  if (config.tcpTransform && tcpIndex === 0) {
    return config.tcpTransform;
  }

  return undefined;
}

function spacerTransform(item: EquipmentItem): CartesianPose | undefined {
  return (item.config as { tcpTransform?: CartesianPose }).tcpTransform;
}

export function buildEndEffectorChain(
  equipmentItems: EquipmentItem[],
): EndEffectorChain {
  const wristMounted = equipmentItems.filter((item) =>
    isWristMounted(item.config.kind),
  );

  const enabledOrdered = wristMounted
    .filter((item) => item.isEnabled)
    .sort((a, b) => compareWristChainOrder(a.config, b.config))
    // At most one spacer per chain slot: the URDF composes one adapter, one
    // sensor, and one bracket; a duplicate row must not compose twice.
    .filter(
      (item, index, sorted) =>
        !isWristChainMember(item.config.kind) ||
        index === 0 ||
        compareWristChainOrder(sorted[index - 1].config, item.config) !== 0,
    );

  const toLink = (item: EquipmentItem): ChainLink => ({
    item,
    kind: item.config.kind,
    role: isWristChainMember(item.config.kind) ? 'spacer' : 'gripper',
  });

  const links = enabledOrdered.map(toLink);

  const grippers = wristMounted
    .filter((item) => isEndEffector(item.config.kind))
    .map(toLink);

  // Disabled chain pieces, kept for id resolution only. A saved frame can name
  // a bracket the robot no longer has enabled, and the picker lists those rows
  // exactly as it lists disabled grippers — so they resolve by id the same way
  // rather than falling to the flange. They never compose: only `links` does.
  const disabledSpacers = wristMounted
    .filter((item) => !item.isEnabled && isWristChainMember(item.config.kind))
    .map(toLink);

  const forceSensor = links.find((link) => isForceSensor(link.kind));

  // The camera row backing the `wristCamera` frame. Cameras are not
  // wrist-mounted equipment, so they never enter `links` and `owner()` cannot
  // name one — the row is found over the whole equipment list instead. The
  // membership test is the tooltip-naming table rather than a kind list of its
  // own, so a kind that starts publishing the option is placed here too.
  //
  // The table is read directly rather than through `optionBelongsToKind`,
  // which is only safe over rows already known to be equipment of a live
  // kind. This search spans every row, and a persisted row can carry a kind
  // the table has no entry for — a retired device, or one this build predates
  // — which has no scheme and so is not a camera.
  const cameraItem = equipmentItems.find(
    (item) =>
      item.isEnabled &&
      TOOLTIP_NAMING_SCHEMES[item.config.kind] === 'wristCamera',
  );

  /**
   * The row a frame names. An id wins when it names a row that also
   * PUBLISHES the option — a stale pairing must not mint a link the URDF
   * never generated. Disabled rows resolve by id. An id that names nothing
   * usable falls through to name resolution: the single ENABLED row whose
   * kind publishes the option. Two rows of one kind are ambiguous without
   * an id; a legacy numeric option (`ee-N`, 1-based, pre-Feb-2024) matches
   * no kind and owns nothing — those values have resolved to the flange
   * since the kind-named cutover, and resolving them positionally now would
   * silently move long-adapted routines.
   */
  const owner = (frame: TCPFrame): ChainLink | undefined => {
    const candidates = [
      ...links,
      ...grippers.filter((g) => !g.item.isEnabled),
      ...disabledSpacers,
    ];

    if (frame.gripperID != null) {
      const byId = candidates.find((link) => link.item.id === frame.gripperID);

      if (byId && optionBelongsToKind(frame.tcpOption, byId.kind)) {
        return byId;
      }
    }

    const owners = links.filter((link) =>
      optionBelongsToKind(frame.tcpOption, link.kind),
    );

    return owners.length === 1 ? owners[0] : undefined;
  };

  /** Enabled spacer transforms strictly below `of` in the chain. */
  const spacersBelow = (of: ChainLink): CartesianPose[] =>
    links
      .filter(
        (link) =>
          link.role === 'spacer' &&
          link.item !== of.item &&
          compareWristChainOrder(link.item.config, of.item.config) < 0,
      )
      .map((link) => spacerTransform(link.item))
      .filter((transform): transform is CartesianPose => transform != null);

  const tcp = (frame: TCPFrame): ResolvedTCP => {
    if (frame.tcpOption === 'wristCamera') {
      const transform =
        cameraItem && getCameraTooltipTransform(cameraItem.config);

      if (transform == null) return FLANGE;

      // The link stays the flange: the URDF mints no camera link, and naming
      // one it does not have is what `ownTipTransform` warns about. The camera
      // is placed by the transform alone, which is the wrist-relative offset
      // its own row defines — it hangs off the wrist rather than sitting in
      // the wrist chain, so no spacer composes below it.
      return { tcpLink: 'tool0', transformFromTool0: transform };
    }

    const tcpIndex = getTCPIndexFromTCPOption(frame.tcpOption);

    if (tcpIndex < 0) return FLANGE;

    const ownerLink = owner(frame);

    if (!ownerLink) return FLANGE;

    const ownTip =
      ownerLink.role === 'spacer'
        ? spacerTransform(ownerLink.item)
        : ownTipTransform(ownerLink.item, tcpIndex);

    if (ownTip == null) return FLANGE;

    let flangeToParent = ZERO_POSE;

    for (const spacer of spacersBelow(ownerLink)) {
      flangeToParent = applyCompoundPose(spacer, flangeToParent);
    }

    const transformFromTool0 = applyCompoundPose(ownTip, flangeToParent);

    return {
      // Every owner names its own frame, spacer or gripper: the URDF mints
      // one per wrist-chain member as well as per gripper tooltip. The link
      // is what the planner rotates about, so a spacer answering `tool0`
      // would turn the arm about the flange while the tip it named sat
      // centimetres away, sweeping an arc instead of spinning in place.
      tcpLink: `${ownerLink.item.id}_tcp_${tcpIndex}`,
      transformFromTool0,
    };
  };

  return { links, grippers, forceSensor, owner, tcp };
}
